import { z } from "zod";

// ---------------------------------------------------------------------------
// 同時編集（#202、docs/82）。
//   ・版の確かめ（楽観ロック）の文言を1か所に（見積書・請求書・領収書・案件・顧客・工程表・スマホ）
//   ・ぶつかったときに「この画面で修正した欄だけ」を最新に重ねる（欄ごとの rebase。見積書の行は estimatePc.ts の rebaseEdits）
//   ・編集中の表示（合図の間隔・消えるまでの時間・チップの文）
// ---------------------------------------------------------------------------

/**
 * 版がぶつかったときの文（docs/82 §5。docs/10 §5.1.2 の言葉の決まり：「〜人」と書かない＝利用者・「読み直す」→ 再読込）。
 * 画面も API の 409 もここから
 */
export const CONFLICT_MSG = {
  /** 見積書・請求書の窓の題（再読込するかを聞く） */
  ask: "他の利用者が変更しました。再読込しますか？",
  /** 窓の説明 */
  note: "再読込すると、他の利用者の変更とこの画面の修正の両方が残ります。",
  reload: "再読込",
  later: "あとで",
  /** 再読込を待って保存を止めている帯 */
  saving: "他の利用者が変更しました（保存していません）",
  /** 再読込のあと、この画面の修正を重ねて保存する */
  merged: "再読込しました。この画面の修正も保存します",
  /** 再読込のあと（重ねる修正が無い） */
  reloaded: "再読込しました",
  /** 自動で再読込した（工程表・スマホの見積） */
  auto: "他の利用者が変更しました。再読込しました",
  /** 自動で再読込して、この画面の修正を重ねて保存した（顧客・案件・領収書） */
  autoMerged: "他の利用者が変更しました。再読込して、この画面の修正を保存しました",
  /** API の 409（*_STALE）の文。画面が自分で扱えなかったときに出る */
  api: "他の利用者が変更しました。再読込してください",
  /** 社内メモの全文の編集が、ほかの追記より古い（重ねられなかった） */
  memoOverwrite: "他の利用者が変更しました。保存するとこの内容で上書きします",
  /** 社内メモの全文の編集に、ほかの追記を末尾に足した */
  memoMerged: "他の利用者の追記を末尾に足しました。確認して保存してください",
} as const;

/** 版（画面が読んだときの updatedAt。ミリ秒までの ISO の文字）。渡すと API がいまの版とくらべる */
export const Version = z.string().min(1).max(40);

/*
 * 欄ごとの rebase は estimatePc.ts の rebaseFields（base と mine で違う欄は mine、同じ欄は theirs＝同じ欄を両方が変えたら
 * この画面の修正が勝つ。docs/82 §1.2 ニ）を顧客・{project}・領収書でも使う
 */

/**
 * 社内メモの全文の編集がぶつかったとき（docs/82 §3）：最新が「この窓を開いたときの文の末尾に足しただけ」なら、
 * 足された分をこの窓の文の末尾に足す（null＝末尾に足しただけではない＝重ねられない）
 */
export function rebaseMemo(base: string, mine: string, theirs: string): string | null {
  if (theirs === base) return mine;
  if (!theirs.startsWith(base)) return null;
  const tail = theirs.slice(base.length);
  return mine === base ? theirs : mine + tail;
}

// ---------------------------------------------------------------------------
// 社内メモの追記（POST /groups/:id/memo/append）
// ---------------------------------------------------------------------------
export const GROUP_MEMO_APPEND_MAX = 2000;
export const GroupMemoAppend = z.object({
  text: z.string().trim().min(1, "追記する内容を入れてください").max(GROUP_MEMO_APPEND_MAX, `追記は${GROUP_MEMO_APPEND_MAX}字までです`),
});
export type GroupMemoAppend = z.infer<typeof GroupMemoAppend>;

/** 追記の頭の印「[2026-10-17 21:05 山田 太郎] 」。時刻は日本時間（API が付ける） */
export function memoStamp(at: Date, name: string): string {
  const j = new Date(at.getTime() + 9 * 3_600_000).toISOString();
  return `[${j.slice(0, 10)} ${j.slice(11, 16)}${name ? ` ${name}` : ""}] `;
}

/** いまのメモの末尾に追記を足した文（メモが空なら頭の改行なし） */
export function appendMemo(memo: string, stamp: string, text: string): string {
  const cur = memo.replace(/\s+$/, "");
  return cur ? `${cur}\n\n${stamp}${text}` : `${stamp}${text}`;
}

export const MEMO_MSG = {
  title: "社内メモ",
  append: "追記",
  appendLabel: "追記する内容",
  edit: "編集",
  editTitle: "社内メモの編集",
  save: "メモ保存",
  cancel: "取消",
  appended: "社内メモに追記しました",
  saved: "社内メモを保存しました",
  empty: "まだメモはありません",
  appending: "追記しています…",
  help: "この会社の中だけのメモです。暗号化して保存し、印字しません。「追記」を押すと、末尾に日時と氏名が付いて足されます。全体を修正するときは「編集」を押します。",
} as const;

// ---------------------------------------------------------------------------
// 編集中（docs/82 §4。表 document_editors）
// ---------------------------------------------------------------------------
/** 合図の間隔（画面がこの間隔で POST /documents/:id/editing を送る） */
export const EDITING_BEAT_MS = 30_000;
/** 合図がこの秒数届かなければ消す（編集中に出さない） */
export const EDITING_TTL_SECONDS = 90;

export const DocumentEditor = z.object({ userId: z.string().uuid(), name: z.string() });
export type DocumentEditor = z.infer<typeof DocumentEditor>;
/** 自分以外の編集中（90 秒以内に合図のあった利用者。並びは名前） */
export const DocumentEditors = z.object({ editors: z.array(DocumentEditor) });
export type DocumentEditors = z.infer<typeof DocumentEditors>;

/** 見出しのチップ「山田 太郎・佐藤 花子が編集中」。いなければ null */
export function editingLabel(names: readonly string[]): string | null {
  const ns = names.map((n) => n.trim()).filter(Boolean);
  return ns.length ? `${ns.join("・")}が編集中` : null;
}
